import {
  Badge,
  ButtonProps,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDebounce } from 'src/hooks/useDebounce';
import { useLocales } from 'src/locales';
import Iconify from '../iconify';
import { StyledBoardSettingsButton } from './styles';

type Props = {
  searchTerm: string;
  onSearch: (inputValue: string) => void;
  clearSearch: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onShowInput?: (visibility: boolean) => void;
  buttonProps?: ButtonProps;
  onlyIcon?: boolean;
};

export const Search = ({
  searchTerm,
  onSearch,
  clearSearch,
  onShowInput,
  buttonProps,
  onlyIcon,
}: Props) => {
  const [showInput, setShowInput] = useState(false);
  const [inputValue, setInputValue] = useState(searchTerm);
  const { translate } = useLocales();
  const searchDebounce = useDebounce<string>(inputValue);

  const onDebounceChange = (value: string) => {
    onSearch(value);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (searchTerm !== inputValue) {
      onDebounceChange(searchDebounce);
    }
  }, [searchDebounce]);

  const handleClearSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
    setInputValue('');
    clearSearch(e);
  };

  const handleShowInput = (visibility: boolean) => {
    setShowInput(visibility);
    if (onShowInput) {
      onShowInput(visibility);
    }
  };

  return (
    <Stack direction="row" flexGrow={1}>
      {showInput ? (
        <ClickAwayListener onClickAway={() => handleShowInput(false)}>
          <TextField
            size="small"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            fullWidth
            autoFocus
            placeholder={translate('shared.search')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify
                    icon="eva:search-fill"
                    sx={{ color: 'text.disabled' }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    sx={{
                      visibility: inputValue.length ? 'visible' : 'hidden',
                    }}
                  >
                    <Iconify
                      icon="tabler:circle-x"
                      sx={{ color: 'text.disabled' }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiInputBase-input': {
                py: 0.8125,
              },
            }}
          />
        </ClickAwayListener>
      ) : (
        <Badge color="secondary" variant="dot" invisible={!inputValue?.length}>
          <Tooltip
            title={translate('shared.buttons.search')}
            placement="top"
            arrow
          >
            {onlyIcon ? (
              <IconButton
                className="button-text"
                onClick={() => handleShowInput(true)}
              >
                <Iconify icon="eva:search-fill" />
              </IconButton>
            ) : (
              <StyledBoardSettingsButton
                {...buttonProps}
                startIcon={<Iconify icon="eva:search-fill" />}
                onClick={() => handleShowInput(true)}
                sx={{ fontWeight: 'fontWeightRegular' }}
              >
                <span className="button-text">
                  {!onlyIcon && translate('shared.buttons.search')}
                </span>
              </StyledBoardSettingsButton>
            )}
          </Tooltip>
        </Badge>
      )}
    </Stack>
  );
};
